import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatedSection } from '../../../components/AnimatedSection';

export default function ContactForm() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      const res = await fetch('https://readdy.ai/api/form/d6vceqt1l1ubpb129elg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatedSection animation="slide-right">
      <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
        <h2 className="text-2xl font-bold text-white mb-2">{t('contact_form_title')}</h2>
        <p className="text-slate-400 text-sm mb-8">{t('contact_form_desc')}</p>

        <form id="it-contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-slate-300 text-sm font-medium mb-2">{t('contact_field_name')} *</label>
              <input
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder={t('contact_placeholder_name')}
                className="w-full bg-slate-900 border border-slate-600 focus:border-cyan-500 text-white placeholder-slate-500 px-4 py-3 rounded-lg text-sm outline-none transition-colors"
              />
            </div>
            <div>
              <label className="block text-slate-300 text-sm font-medium mb-2">{t('contact_field_company')} *</label>
              <input
                type="text"
                name="company"
                required
                value={formData.company}
                onChange={handleChange}
                placeholder={t('contact_placeholder_company')}
                className="w-full bg-slate-900 border border-slate-600 focus:border-cyan-500 text-white placeholder-slate-500 px-4 py-3 rounded-lg text-sm outline-none transition-colors"
              />
            </div>
          </div>

          <div>
            <label className="block text-slate-300 text-sm font-medium mb-2">{t('contact_field_email')} *</label>
            <input
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder={t('contact_placeholder_email')}
              className="w-full bg-slate-900 border border-slate-600 focus:border-cyan-500 text-white placeholder-slate-500 px-4 py-3 rounded-lg text-sm outline-none transition-colors"
            />
          </div>

          <div>
            <label className="block text-slate-300 text-sm font-medium mb-2">{t('contact_field_message')}</label>
            <textarea
              name="message"
              rows={4}
              maxLength={500}
              value={formData.message}
              onChange={handleChange}
              placeholder={t('contact_placeholder_message')}
              className="w-full bg-slate-900 border border-slate-600 focus:border-cyan-500 text-white placeholder-slate-500 px-4 py-3 rounded-lg text-sm outline-none transition-colors resize-none"
            />
            <div className={`text-xs text-right mt-1 ${formData.message.length > 500 ? 'text-red-400' : 'text-slate-500'}`}>
              {formData.message.length}/500
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting || formData.message.length > 500}
            className="w-full bg-cyan-500 hover:bg-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 font-bold py-4 rounded-lg text-sm transition-all whitespace-nowrap cursor-pointer"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i>
                {t('contact_submitting')}
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                {t('contact_submit')}
                <i className="ri-send-plane-line"></i>
              </span>
            )}
          </button>

          {submitStatus === 'success' && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 px-4 py-3 rounded-lg flex items-start gap-3">
              <i className="ri-checkbox-circle-fill text-xl shrink-0 mt-0.5"></i>
              <div>
                <div className="font-semibold text-sm">{t('contact_success_title')}</div>
                <div className="text-xs mt-0.5 text-emerald-500/80">{t('contact_success_desc')}</div>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg flex items-start gap-3">
              <i className="ri-error-warning-fill text-xl shrink-0 mt-0.5"></i>
              <div>
                <div className="font-semibold text-sm">{t('contact_error_title')}</div>
                <div className="text-xs mt-0.5 text-red-400/80">{t('contact_error_desc')}</div>
              </div>
            </div>
          )}
        </form>
      </div>
    </AnimatedSection>
  );
}
